import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Accordion } from 'edenred-ui';
import { useFormContext } from 'react-hook-form';

import { FormFields } from '@epi-forms/helpers';
import { CreditCardMethodProps } from '@epi-models/forms/PaymentMethod';
import { formatNumeric } from '@epi-helpers/numeral';
import { PaymentMethods } from '@epi-constants/paymentMethods';

import { ExternalPayment } from '../ExternalPayment/ExternalPayment';
import { PaymentMethodField } from '../PaymentMethodField/PaymentMethodField';
import { PaymentInfo } from '../PaymentInfo';

import { SingleCard } from './SingleCard';

export function CreditCardMethod({
  disabled,
  creditCardFeePercent = 0,
  onClickPaymentMethod,
  creditCardMethods,
  isLoading,
  isError,
  isPaymentProcessing = false,
  getPaymentMinValue,
  onPaymentMethodChanged = () => {},
  amountValue,
  isMasterAccount
}: CreditCardMethodProps) {
  const { t } = useTranslation();
  const { watch } = useFormContext();

  const method = watch(FormFields.PaymentMethod);

  const isLessThanMinValue = (paymentMethod: string) => {
    return amountValue < getPaymentMinValue(paymentMethod);
  };

  return (
    <Box display="flex" alignItems="center">
      <Box display={isMasterAccount ? 'flex' : 'block'} alignItems="center">
        <Box
          display="flex"
          alignItems="center"
          data-test="cc-payment-method-checkbox"
        >
          <PaymentMethodField
            disabled={disabled}
            label={t('forms.payment_method.credit_card_payment')}
            name={FormFields.PaymentMethod}
            value={PaymentMethods.CreditCard}
            onChangeMethod={onPaymentMethodChanged}
          />
          <PaymentInfo title="forms.payment_method.invoicing_info" />
        </Box>
        <Box flex={1}>
          <Accordion
            hideArrow
            summaryChild={<span />}
            expanded={method === PaymentMethods.CreditCard}
            detailsChild={
              <Box
                className="credit_card"
                id="credit-card-method-accordion"
                display="flex"
                alignItems="center"
              >
                {isMasterAccount ? (
                  <SingleCard
                    isError={
                      isError || isLessThanMinValue(PaymentMethods.CreditCard)
                    }
                    isLoading={isLoading}
                    disabled={disabled}
                    method={creditCardMethods[0]}
                    onClickPaymentMethod={onClickPaymentMethod}
                    isProcessing={isPaymentProcessing}
                  />
                ) : (
                  <ExternalPayment
                    isError={
                      isError || isLessThanMinValue(PaymentMethods.CreditCard)
                    }
                    isLoading={isLoading}
                    disabled={disabled}
                    methods={creditCardMethods}
                    onClickPaymentMethod={onClickPaymentMethod}
                    isProcessing={isPaymentProcessing}
                  />
                )}
                {creditCardFeePercent > 0 && (
                  <PaymentInfo
                    title={t(`forms.payment_method.credit_card_fee_info`, {
                      value: formatNumeric(creditCardFeePercent, '0.00')
                    })}
                  />
                )}
              </Box>
            }
          />
        </Box>
      </Box>
    </Box>
  );
}
